import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import {
  FailedCard,
  fileStem,
  GeneratedDrawer,
  GeneratedGallery,
  plural,
  ResultCard,
} from '../GeneratedGallery.js';
import { Copy } from '../primitives.js';
import { playableUrl } from '../video/videoResult.js';
import type { GeneratedBanner, GeneratedBannerResult } from './bannerResult.js';

const FALLBACK =
  'The banner preview is unavailable. Reopen this outcome to refresh its preview link.';
const NAV_BUTTON_SX = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 0.8,
  '&:hover': { bgcolor: 'background.paper' },
};
// A fixed frame, so paging between a tall and a wide size does not move the pager.
const DRAWER_FRAME_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  height: { xs: 360, sm: 520 },
  p: 2,
  borderRadius: 2,
  bgcolor: 'background.paper',
};

// One take of a version: the service draws it once and adapts it to every size.
interface Take {
  key: string;
  sizes: [GeneratedBanner, ...GeneratedBanner[]];
}

function takesOf(banners: GeneratedBanner[]): Take[] {
  const takes = new Map<string, Take>();
  for (const banner of banners) {
    const key = `${banner.version_name}|${banner.variation}|${banner.references_used}`;
    const take = takes.get(key);
    if (take === undefined) takes.set(key, { key, sizes: [banner] });
    else take.sizes.push(banner);
  }
  return [...takes.values()];
}

function bannerLabel(banner: GeneratedBanner): string {
  const references = banner.references_used ? ' with references' : '';
  return `${banner.version_name} ${banner.size} #${banner.variation}${references}`;
}

function takeDetails(take: Take): string {
  const [first] = take.sizes;
  return [
    plural(take.sizes.length, 'size'),
    `#${first.variation}`,
    ...(first.references_used ? ['With references'] : []),
  ].join(' · ');
}

function strategyLabel(banner: GeneratedBanner): string {
  return banner.version?.strategy_label || banner.strategy;
}

// Signed URL only; an expired link falls back to the refresh hint instead of a broken image.
function BannerImage({ banner, sx }: { banner: GeneratedBanner; sx: SxProps<Theme> }): ReactNode {
  const url = playableUrl(banner.playback_url);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (banner.status === 'failed') {
    return (
      <Alert severity="error">{banner.error ?? `${banner.size} could not be rendered.`}</Alert>
    );
  }
  if (url === null || failedUrl === url) return <Alert severity="warning">{FALLBACK}</Alert>;
  return (
    <Box
      component="img"
      src={url}
      alt={bannerLabel(banner)}
      onError={() => setFailedUrl(url)}
      sx={[{ width: '100%', objectFit: 'contain' }, ...[sx].flat()]}
    />
  );
}

// Same pager as the brief navigator: chevrons either side, the size, and a dot per size.
function SizePager({
  sizes,
  at,
  onSelect,
}: {
  sizes: GeneratedBanner[];
  at: number;
  onSelect: (index: number) => void;
}): ReactNode {
  return (
    <Stack
      direction="row"
      onClick={(event) => event.stopPropagation()}
      sx={{ alignItems: 'center', justifyContent: 'center', gap: 1 }}
    >
      <IconButton
        size="small"
        aria-label="Previous size"
        disabled={at === 0}
        onClick={() => onSelect(at - 1)}
        sx={NAV_BUTTON_SX}
      >
        <ChevronLeft fontSize="small" />
      </IconButton>
      <Stack sx={{ alignItems: 'center', minWidth: 96 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{sizes[at]?.size}</Typography>
        <Box sx={{ display: 'flex', gap: 0.6, mt: 0.5 }}>
          {sizes.map((banner, index) => (
            <Box
              key={banner.size}
              component="button"
              type="button"
              aria-label={`Show ${banner.size}`}
              onClick={() => onSelect(index)}
              sx={{
                p: 0,
                border: 0,
                cursor: 'pointer',
                height: 6,
                width: index === at ? 16 : 6,
                borderRadius: 999,
                bgcolor: index === at ? 'primary.main' : 'divider',
                transition: 'width .2s ease, background-color .2s ease',
              }}
            />
          ))}
        </Box>
      </Stack>
      <IconButton
        size="small"
        aria-label="Next size"
        disabled={at === sizes.length - 1}
        onClick={() => onSelect(at + 1)}
        sx={NAV_BUTTON_SX}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function TakeCard({ take, onOpen }: { take: Take; onOpen: (size: number) => void }): ReactNode {
  const [at, setAt] = useState(0);
  const banner = take.sizes[at] ?? take.sizes[0];
  return (
    <ResultCard
      strategy={banner.strategy}
      strategyLabel={strategyLabel(banner)}
      title={banner.version_name}
      subtitle={takeDetails(take)}
      mediaSx={{ aspectRatio: '1 / 1', bgcolor: 'background.page' }}
    >
      <Box
        component="button"
        type="button"
        aria-label={`Open ${bannerLabel(banner)}`}
        onClick={() => onOpen(at)}
        sx={{
          position: 'absolute',
          inset: 0,
          p: 2,
          pb: 8,
          border: 0,
          bgcolor: 'transparent',
          cursor: 'pointer',
        }}
      >
        <BannerImage banner={banner} sx={{ height: '100%' }} />
      </Box>
      {take.sizes.length > 1 ? (
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 12 }}>
          <SizePager sizes={take.sizes} at={at} onSelect={setAt} />
        </Box>
      ) : null}
    </ResultCard>
  );
}

export function GeneratedBannerGallery({ result }: { result: GeneratedBannerResult }): ReactNode {
  const takes = takesOf(result.banners);
  const ready = takes.filter((take) => take.sizes.some((banner) => banner.status === 'done'));
  const failed = result.banners.filter((banner) => banner.status === 'failed').length;
  const done = result.banners.length - failed;
  const [active, setActive] = useState<number | null>(null);
  const [activeSize, setActiveSize] = useState(0);
  const open = (take: number, size: number): void => {
    setActive(take);
    setActiveSize(size);
  };

  return (
    <GeneratedGallery
      noun="banner"
      ready={done}
      failed={failed}
      readyLine={`${plural(done, 'banner')} ready`}
      summary={result.summary}
      gridTestId="generated-banner-grid"
      onView={() => open(0, 0)}
      drawer={
        <GeneratedDrawer
          items={ready}
          active={active}
          noun="banner"
          testId="generated-banner-drawer"
          onClose={() => setActive(null)}
          onMove={(take) => open(take, 0)}
          render={(take) => {
            const banner = take.sizes[activeSize] ?? take.sizes[0];
            return {
              title: banner.version_name,
              subtitle: takeDetails(take),
              body: (
                <>
                  {banner.warnings.map((warning) => (
                    <Alert key={warning} severity="warning">
                      {warning}
                    </Alert>
                  ))}
                  <Box sx={DRAWER_FRAME_SX}>
                    <BannerImage banner={banner} sx={{ height: '100%' }} />
                  </Box>
                  {take.sizes.length > 1 ? (
                    <SizePager sizes={take.sizes} at={activeSize} onSelect={setActiveSize} />
                  ) : null}
                  <Stack direction="row" sx={{ gap: 5, flexWrap: 'wrap' }}>
                    <Copy label="Size">{banner.size}</Copy>
                    <Copy label="Strategy">{strategyLabel(banner)}</Copy>
                  </Stack>
                  <Copy label="Headline">{banner.version?.banner.headline}</Copy>
                  <Copy label="Subline">{banner.version?.banner.subline}</Copy>
                  <Copy label="CTA">{banner.version?.banner.cta}</Copy>
                </>
              ),
              download: {
                href: banner.status === 'done' ? playableUrl(banner.playback_url) : null,
                fileName: `${fileStem(banner.version_name, 'banner')}-${banner.size}-${banner.variation}.png`,
                label: `Download ${banner.size}`,
              },
            };
          }}
        />
      }
    >
      {takes.map((take) =>
        ready.includes(take) ? (
          <TakeCard key={take.key} take={take} onOpen={(size) => open(ready.indexOf(take), size)} />
        ) : (
          <FailedCard
            key={take.key}
            name={take.sizes[0].version_name}
            error={take.sizes[0].error}
          />
        ),
      )}
    </GeneratedGallery>
  );
}
